<template>
    <div class="m-dropdown">
        <!-- Call to action div with selected label -->
        <div
            class="dropdown-cta px-3 py-3 d-flex align-items-center justify-content-between"
            @click="toggleDropdown"
        >
            <p
                class="selected-label geist-font regular knights-cloak-80--text overflow-ellipsis"
                style="font-weight: 500"
            >
                {{ selectedOption ? selectedOption.label : placeholder }}
            </p>
            <font-awesome-icon
                class="dropdown-chev castle-stone-600--text"
                icon="fas fa-chevron-down"
                size="sm"
                :class="{ rotated: isOpen }"
            />
        </div>

        <transition name="dropdown-fade">
            <div
                v-if="isOpen"
                class="dropdown-options"
                v-click-outside="
                    () => {
                        if (isOpen) {
                            isOpen = false
                        }
                    }
                "
            >
                <div
                    v-for="(option, index) in options"
                    :key="index"
                    class="dropdown-option px-2 d-flex align-items-center"
                    @click="selectOption(option)"
                >
                    <div
                        class="inner d-flex align-items-center pa-2 w-full"
                        :class="{ selected: isOptionSelected(option) }"
                    >
                        <div class="check-holder d-flex align-items-center">
                            <font-awesome-icon
                                v-if="isOptionSelected(option)"
                                class="selected-icon castle-moat--text"
                                icon="fas fa-check"
                            />
                        </div>
                        <p class="geist-font knights-cloak-80--text">{{ option.label }}</p>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, watch, computed, defineEmits } from 'vue'

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: [String, Number, Object],
        default: null,
    },
    placeholder: {
        type: String,
        default: 'Select an option',
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const selectedOption = ref(null)

// Find the initially selected option based on modelValue
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            selectedOption.value = props.options.find(
                (option) => option.value === newValue || option === newValue
            )
        } else {
            selectedOption.value = null
        }
    },
    { immediate: true }
)

// Toggle dropdown visibility
const toggleDropdown = () => {
    isOpen.value = !isOpen.value
}

const isOptionSelected = (option) => {
    if (!selectedOption.value) return false

    return option.value !== undefined
        ? option.value === selectedOption.value.value
        : option === selectedOption.value
}

// Handle option selection
const selectOption = (option) => {
    selectedOption.value = option
    isOpen.value = false

    // Emit the selected value (support for v-model)
    emit('update:modelValue', option.value !== undefined ? option.value : option)

    // Also emit a change event with the full option object
    emit('change', option)
}
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.m-dropdown {
    position: relative;
    width: 100%;
    max-width: 250px;

    .dropdown-cta {
        border-radius: 4px;
        cursor: pointer;
        background: rgba(104, 110, 115, 0.1);

        .dropdown-chev {
            transition: transform 0.3s ease-in-out;

            &.rotated {
                transform: rotate(180deg);
            }
        }
    }

    .dropdown-options {
        position: absolute;
        top: calc(100% + 10px);
        left: 0;
        width: 100%;
        max-height: 200px;
        overflow-y: auto;
        background-color: white;
        border-radius: 4px;
        z-index: 10;
        box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
        padding-top: 8px;

        .dropdown-option {
            padding-bottom: 8px;
            .inner {
                cursor: pointer;
                border-radius: 4px;
                transition: all 0.3s ease-in-out;
                &:hover {
                    background-color: vars.$grey05;
                }

                .check-holder {
                    min-width: 25px;
                }

                &.selected {
                    background-color: vars.$grey05;
                }
            }
        }
    }

    .dropdown-fade-enter-active,
    .dropdown-fade-leave-active {
        transition:
            opacity 0.3s,
            transform 0.3s;
    }

    .dropdown-fade-enter-from,
    .dropdown-fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
